import { useRouteOrganizationId } from '@/hooks/useRouteOrganizationId'
import queryClient from '@/lib/queryClient'
import { ProductStatus } from '@/lib/enums/ProductStatus'
import { indexTableQueryOptions } from '@/lib/services/index-table-query-options'
import { QueryFilters } from '@/lib/utils'
import {
  buildAdminQueryRoot,
  buildScopedAdminQueryKey,
  type AdminQueryKey,
  type AdminQueryRoot,
  type OrganizationIdInput,
} from '@/lib/organization-scoping'
import {
  infiniteQueryOptions,
  queryOptions,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useSuspenseQuery,
} from '@tanstack/react-query'
import { useEffect, useEffectEvent } from 'react'
import { addOnSetQueryKeys } from '../add-on-sets/addOnSets.keys'
import { collectionQueryKeys } from '../collections/collections.query'
import { schedulePriceAdjustmentBoundaryCallback } from '../price-adjustments/priceAdjustments.query'
import {
  createProduct,
  getInfiniteProducts,
  getProduct,
  getProductSearchTags,
  getProducts,
  recommendProductSearchTags,
  softDeleteProduct,
  updateProduct,
  type ProductIndexQueryKeyEntries,
  type ProductOrganizationScope,
  type ProductScope,
  type ProductInfiniteQueryKeyEntries,
  type ProductSearchTagsQueryKeyEntries,
  type ProductShowQueryKeyEntries,
  type RecommendProductSearchTagsValues,
} from './products.api'
import { inventoryQueryKeys } from '../inventory/inventory.keys'
import type {
  CreateProductRequest,
  ProductIdInput,
  ProductIndexFilters,
  ProductShowFilters,
  UpdateProductRequest,
} from './products.types'

/**
 * Hooks fall back to the route's organization; the `all` key without one matches every
 * organization's products; the scoped key builders throw without one.
 */
interface OrganizationScope {
  organizationId?: OrganizationIdInput
}

interface ProductIndexScope extends OrganizationScope {
  filters?: ProductIndexFilters | undefined
}

interface ProductInfiniteScope extends ProductIndexScope {
  pageSize?: number | undefined
}

/** The show key names one product. */
interface ProductShowKeyScope extends OrganizationScope {
  productId: ProductIdInput
  filters?: ProductShowFilters | undefined
}

/** Query options build requests, which need the organization. */
type ProductIndexQueryScope = ProductOrganizationScope & {
  filters?: ProductIndexFilters | undefined
}

type ProductInfiniteQueryScope = ProductIndexQueryScope & {
  pageSize?: number | undefined
}

type ProductShowQueryScope = ProductScope & {
  filters?: ProductShowFilters | undefined
}

/** The key entries each fetcher reads; the scoped key builders below set them. */
type ProductIndexQueryKey = [AdminQueryRoot & ProductIndexQueryKeyEntries]
type ProductInfiniteQueryKey = [AdminQueryRoot & ProductInfiniteQueryKeyEntries]
type ProductShowQueryKey = [AdminQueryRoot & ProductShowQueryKeyEntries]
type ProductSearchTagsQueryKey = [AdminQueryRoot & ProductSearchTagsQueryKeyEntries]

/** Query options a caller may override; the key and fetcher stay owned by this module. */
type QueryParams<TOptions> = Partial<Omit<TOptions, 'queryKey' | 'queryFn'>>

export const productFilters = () =>
  new QueryFilters({
    include: ['variants', 'category', 'collections', 'media', 'addOnSets'],
  })

export const designProductsFilters = new QueryFilters({
  include: ['media'],
})
  .applyFilter({
    exclude_add_on_only: true,
    status: ProductStatus.Active.Value,
    storefront_visibility: 'shop_all',
    sellable_only: true,
  })
  .get()

export const productQueryKeys = {
  all: ({ organizationId }: OrganizationScope = {}): AdminQueryKey => [
    buildAdminQueryRoot({ entity: 'product' }, { organizationId }),
  ],
  index: ({ organizationId, filters }: ProductIndexScope): ProductIndexQueryKey =>
    buildScopedAdminQueryKey(productQueryKeys.all({ organizationId })[0], {
      organizationId,
      scope: 'index',
      filters,
    }),
  infinite: ({
    organizationId,
    filters,
    pageSize,
  }: ProductInfiniteScope): ProductInfiniteQueryKey =>
    buildScopedAdminQueryKey(productQueryKeys.all({ organizationId })[0], {
      organizationId,
      scope: 'infinite',
      filters,
      pageSize,
    }),
  show: ({ organizationId, productId, filters }: ProductShowKeyScope): ProductShowQueryKey => {
    const [entries] = buildScopedAdminQueryKey(productQueryKeys.all({ organizationId })[0], {
      organizationId,
      scope: 'show',
      filters,
    })

    // Set after the shared builder, whose loosely typed entries would make the id optional.
    return [{ ...entries, productId }]
  },
  searchTags: ({ organizationId }: OrganizationScope): ProductSearchTagsQueryKey =>
    buildScopedAdminQueryKey(productQueryKeys.all({ organizationId })[0], {
      organizationId,
      scope: 'search-tags',
    }),
}

export const productQueries = {
  index: ({ organizationId, filters }: ProductIndexQueryScope) =>
    indexTableQueryOptions({
      queryKey: productQueryKeys.index({ organizationId, filters }),
      queryFn: getProducts,
    }),

  infinite: ({ organizationId, filters, pageSize }: ProductInfiniteQueryScope) =>
    infiniteQueryOptions({
      queryKey: productQueryKeys.infinite({ organizationId, filters, pageSize }),
      queryFn: getInfiniteProducts,
      initialPageParam: 1,
      getNextPageParam: (lastPage) => {
        const meta = Array.isArray(lastPage) ? undefined : lastPage?.meta
        const currentPage = Number(meta?.current_page ?? 1)
        const lastPageNumber = Number(meta?.last_page ?? 1)

        return currentPage < lastPageNumber ? currentPage + 1 : undefined
      },
    }),

  show: ({ organizationId, productId, filters }: ProductShowQueryScope) =>
    queryOptions({
      queryKey: productQueryKeys.show({ organizationId, productId, filters }),
      queryFn: getProduct,
    }),

  searchTags: ({ organizationId }: ProductOrganizationScope) =>
    queryOptions({
      queryKey: productQueryKeys.searchTags({ organizationId }),
      queryFn: getProductSearchTags,
      staleTime: 1000 * 60 * 15,
    }),
}

function useRefetchProductsAtBoundary({
  organizationId,
  boundaryAt,
  dedupeKey,
}: {
  organizationId: number
  boundaryAt: string | null | undefined
  dedupeKey: string
}) {
  const refreshProducts = useEffectEvent(async () => {
    if (!organizationId) {
      return
    }

    // Reject when the refetch fails so the boundary scheduler clears its dedupe and the next mount retries.
    await queryClient.invalidateQueries(
      { queryKey: productQueryKeys.all({ organizationId }) },
      { throwOnError: true }
    )
  })

  useEffect(() => {
    if (!organizationId || !boundaryAt) {
      return
    }

    return schedulePriceAdjustmentBoundaryCallback({
      boundaryAt,
      onBoundary: refreshProducts,
      dedupeKey: `products:${dedupeKey}`,
    })
  }, [boundaryAt, dedupeKey, organizationId])
}

export function useProducts({
  organizationId,
  filters,
  params = {},
}: ProductIndexScope & { params?: QueryParams<ReturnType<typeof productQueries.index>> } = {}) {
  const resolvedOrganizationId = useRouteOrganizationId(organizationId)

  const query = useQuery({
    ...productQueries.index({ organizationId: resolvedOrganizationId, filters }),
    ...params,
  })

  useRefetchProductsAtBoundary({
    organizationId: resolvedOrganizationId,
    boundaryAt: query.data?.nextPriceAdjustmentChangeAt,
    dedupeKey: `index:${resolvedOrganizationId}`,
  })

  return query
}

export function useInfiniteProducts({
  organizationId,
  filters,
  pageSize,
  ...params
}: ProductInfiniteScope & QueryParams<ReturnType<typeof productQueries.infinite>> = {}) {
  const resolvedOrganizationId = useRouteOrganizationId(organizationId)

  const query = useInfiniteQuery({
    ...productQueries.infinite({
      organizationId: resolvedOrganizationId,
      filters,
      pageSize,
    }),
    ...params,
  })

  useRefetchProductsAtBoundary({
    organizationId: resolvedOrganizationId,
    boundaryAt: query.data?.pages?.find((page) => page?.nextPriceAdjustmentChangeAt)
      ?.nextPriceAdjustmentChangeAt,
    dedupeKey: `infinite:${resolvedOrganizationId}`,
  })

  return query
}

export function useProduct(
  productId: ProductIdInput,
  {
    organizationId,
    filters,
    ...params
  }: OrganizationScope & {
    filters?: ProductShowFilters | undefined
  } & QueryParams<ReturnType<typeof productQueries.show>> = {}
) {
  const resolvedOrganizationId = useRouteOrganizationId(organizationId)

  const query = useSuspenseQuery({
    ...productQueries.show({
      organizationId: resolvedOrganizationId,
      productId,
      filters,
    }),
    ...params,
  })

  useRefetchProductsAtBoundary({
    organizationId: resolvedOrganizationId,
    boundaryAt: query.data?.nextPriceAdjustmentChangeAt,
    dedupeKey: `show:${resolvedOrganizationId}:${productId}`,
  })

  return query
}

export function useProductSearchTags({
  organizationId,
  params = {},
}: OrganizationScope & {
  params?: QueryParams<ReturnType<typeof productQueries.searchTags>>
} = {}) {
  const resolvedOrganizationId = useRouteOrganizationId(organizationId)

  return useQuery({
    ...productQueries.searchTags({ organizationId: resolvedOrganizationId }),
    placeholderData: [],
    ...params,
  })
}

export function useRecommendProductSearchTagsMutation() {
  const organizationId = useRouteOrganizationId()

  return useMutation({
    mutationFn: (values: RecommendProductSearchTagsValues) =>
      recommendProductSearchTags({ organizationId, ...values }),
    onSuccess: (_, values) => {
      if (values?.apply === false || values?.draft) {
        return undefined
      }

      return Promise.all([
        queryClient.invalidateQueries({ queryKey: productQueryKeys.all({ organizationId }) }),
        values?.productId
          ? queryClient.invalidateQueries({
              queryKey: productQueryKeys.show({ organizationId, productId: values.productId }),
            })
          : undefined,
      ])
    },
  })
}

function invalidateProductDependencies(organizationId: number) {
  return Promise.all([
    queryClient.invalidateQueries({ queryKey: productQueryKeys.all({ organizationId }) }),
    queryClient.invalidateQueries({ queryKey: collectionQueryKeys.all({ organizationId }) }),
    queryClient.invalidateQueries({ queryKey: addOnSetQueryKeys.all({ organizationId }) }),
    queryClient.invalidateQueries({ queryKey: inventoryQueryKeys.all({ organizationId }) }),
  ])
}

export function useUpdateProductMutation() {
  const organizationId = useRouteOrganizationId()
  return useMutation({
    mutationFn: (values: UpdateProductRequest & { productId: ProductIdInput }) =>
      updateProduct({ organizationId, ...values }),
    onSuccess: () => invalidateProductDependencies(organizationId),
  })
}

export function useCreateProductMutation() {
  const organizationId = useRouteOrganizationId()
  return useMutation({
    mutationFn: (values: CreateProductRequest) => createProduct({ organizationId, ...values }),
    onSuccess: () => invalidateProductDependencies(organizationId),
  })
}

const cleanupDeletedProductQueries = ({
  organizationId,
  productId,
}: {
  organizationId: number
  productId: ProductIdInput
}) => {
  queryClient.removeQueries({
    queryKey: productQueryKeys.show({ organizationId, productId }),
  })

  return invalidateProductDependencies(organizationId)
}

export function useSoftDeleteProductMutation({
  cleanupOnSuccess = true,
}: {
  /** False for callers that clean up after navigating away (`cleanupDeletedProduct`). */
  cleanupOnSuccess?: boolean
} = {}) {
  const organizationId = useRouteOrganizationId()
  const cleanupDeletedProduct = (productId: ProductIdInput) =>
    cleanupDeletedProductQueries({ organizationId, productId })

  const mutation = useMutation({
    mutationFn: (productId: ProductIdInput) => softDeleteProduct({ organizationId, productId }),
    onSuccess: (_, productId) => {
      if (!cleanupOnSuccess) {
        return undefined
      }

      return cleanupDeletedProduct(productId)
    },
  })

  return {
    ...mutation,
    cleanupDeletedProduct,
  }
}
